import React, { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Portal } from './Portal.jsx';
import { tabsOutOf, useAnchoredPopover } from './usePopover.js';
import { CalendarHeader, CalendarMonth, YearGrid } from './Calendar.jsx';
import {
  formatDisplayDate,
  monthOf,
  outOfBounds,
  parseDisplayDate,
  shiftMonth,
  validIso,
} from '../../lib/dateInput.js';
import { isoDate } from '../../lib/time.js';

// The Steel Glass replacement for <input type="date">: a recessed field that
// reads and takes "DD.MM.YYYY", and a calendar popover (Monday first, today
// ringed, a year view behind the month title, Today and Clear in the footer).
// The value stays an ISO "YYYY-MM-DD" string, or '' when empty — what the
// native input reported — so callers keep their state and API shapes.
export function DatePicker({
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  ariaLabel,
  id,
  placeholder = 'DD.MM.YYYY',
  className = '',
  autoFocus = false,
}) {
  const reactId = useId();
  const baseId = id || `date-${reactId}`;
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const iso = validIso(value) || '';
  const [text, setText] = useState(formatDisplayDate(iso));
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => monthOf(iso));
  const [yearView, setYearView] = useState(false);
  const [yearPage, setYearPage] = useState(() => monthOf(iso).year - 5);
  const [focusDate, setFocusDate] = useState(iso || isoDate(new Date()));
  const editing = useRef(false);
  const refocusDay = useRef(false);

  // Follow outside changes while the person is not typing.
  useEffect(() => {
    if (!editing.current) {
      setText(formatDisplayDate(iso));
      setInvalid(false);
    }
  }, [iso]);

  // A flagged entry also fails the form's native validation, so a submit
  // cannot quietly save the value the field still holds underneath.
  useEffect(() => {
    inputRef.current?.setCustomValidity(
      invalid ? 'Enter a date as DD.MM.YYYY' : '',
    );
  }, [invalid]);

  // A year pick swaps the focused year button for the month grid; hand focus
  // to the month's roving day (or the title) so the keyboard stays inside.
  useEffect(() => {
    if (yearView || !refocusDay.current) return;
    refocusDay.current = false;
    const popover = popoverRef.current;
    (
      popover?.querySelector('.cal-day[tabindex="0"]:not(:disabled)') ||
      popover?.querySelector('.cal-title')
    )?.focus();
  }, [yearView]);

  const style = useAnchoredPopover({
    open,
    onClose: (reason) => close(reason === 'escape'),
    anchorRef: fieldRef,
    popoverRef,
  });

  function openCalendar() {
    if (disabled || open) return;
    const start = iso || isoDate(new Date());
    setView(monthOf(start));
    setYearPage(monthOf(start).year - 5);
    setYearView(false);
    setFocusDate(start);
    setOpen(true);
  }

  function close(returnFocus = false) {
    setOpen(false);
    if (returnFocus) inputRef.current?.focus();
  }

  function emit(next) {
    if (next !== iso) onChange(next);
  }

  function pick(next) {
    if (outOfBounds(next, min, max)) return;
    editing.current = false;
    setText(formatDisplayDate(next));
    setInvalid(false);
    emit(next);
    close(true);
  }

  // Commit what was typed: a real date, or '' when the field was cleared and
  // the value is optional. Anything else is flagged and the value kept.
  function commitText() {
    editing.current = false;
    const trimmed = text.trim();
    if (!trimmed) {
      if (required) {
        setText(formatDisplayDate(iso));
        setInvalid(false);
      } else {
        setInvalid(false);
        emit('');
      }
      return true;
    }
    const parsed = parseDisplayDate(trimmed);
    if (!parsed || outOfBounds(parsed, min, max)) {
      setInvalid(true);
      return false;
    }
    setInvalid(false);
    setText(formatDisplayDate(parsed));
    emit(parsed);
    return true;
  }

  function onFocusDate(next) {
    setFocusDate(next);
    const target = monthOf(next);
    if (target.year !== view.year || target.month !== view.month)
      setView(target);
  }

  return (
    <div
      ref={fieldRef}
      className={`date-field${invalid ? ' is-invalid' : ''}${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''} ${className}`.trim()}
    >
      <input
        ref={inputRef}
        id={baseId}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={openCalendar}
        onChange={(event) => {
          editing.current = true;
          setText(event.target.value);
          setInvalid(false);
          const parsed = parseDisplayDate(event.target.value);
          if (parsed && open) onFocusDate(parsed);
        }}
        onBlur={(event) => {
          // Commit even when focus only moves into the calendar: from there
          // it can leave for a Save button without passing back through here,
          // and the typed date (or its invalid flag) must not be lost.
          commitText();
          if (popoverRef.current?.contains(event.relatedTarget)) return;
          // Focus moved on to another control (e.g. Tab): the calendar
          // belongs to this field, so it closes with it.
          if (event.relatedTarget) close(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (commitText()) close(false);
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) openCalendar();
            else
              popoverRef.current
                ?.querySelector('.cal-day[tabindex="0"]')
                ?.focus();
          }
        }}
      />
      <button
        type="button"
        className="date-field-icon"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Open calendar"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) close(true);
          else {
            inputRef.current?.focus();
            openCalendar();
          }
        }}
      >
        <CalendarDays size={20} aria-hidden="true" />
      </button>
      {open && (
        <Portal>
          <div
            ref={popoverRef}
            className="date-popover"
            role="dialog"
            aria-label={ariaLabel ? `${ariaLabel} calendar` : 'Calendar'}
            style={style}
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (tabsOutOf(event, popoverRef.current)) close(true);
            }}
          >
            {yearView ? (
              <>
                <CalendarHeader
                  id={`${baseId}-title`}
                  year={view.year}
                  month={view.month}
                  title={`${yearPage} – ${yearPage + 11}`}
                  yearView
                  onTitleClick={() => setYearView(false)}
                  onPrev={() => setYearPage((y) => y - 12)}
                  onNext={() => setYearPage((y) => y + 12)}
                />
                <YearGrid
                  firstYear={yearPage}
                  selectedYear={view.year}
                  onPick={(year) => {
                    refocusDay.current = popoverRef.current?.contains(
                      document.activeElement,
                    );
                    setView((v) => ({ ...v, year }));
                    setYearView(false);
                  }}
                />
              </>
            ) : (
              <>
                <CalendarHeader
                  id={`${baseId}-title`}
                  year={view.year}
                  month={view.month}
                  onTitleClick={() => {
                    setYearPage(view.year - 5);
                    setYearView(true);
                  }}
                  onPrev={() => setView((v) => shiftMonth(v, -1))}
                  onNext={() => setView((v) => shiftMonth(v, 1))}
                />
                <CalendarMonth
                  year={view.year}
                  month={view.month}
                  selected={iso}
                  min={min}
                  max={max}
                  focusDate={focusDate}
                  onFocusDate={onFocusDate}
                  onPick={pick}
                  labelledBy={`${baseId}-title`}
                />
              </>
            )}
            <div className="date-popover-foot">
              <button
                type="button"
                className="text-button"
                disabled={outOfBounds(isoDate(new Date()), min, max)}
                onClick={() => pick(isoDate(new Date()))}
              >
                Today
              </button>
              {!required && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    editing.current = false;
                    setText('');
                    setInvalid(false);
                    emit('');
                    close(true);
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
